import { OptionEntry } from "../types/cqa";

// Helper function to check if user is assigned as an auditor
export const filterProjectsByAuditors = (qaRule: any, workerId: string): boolean => {
    const auditors = qaRule.data.project_configuration.auditors;
    
    // Only return true if auditors exist AND the user is in the auditors list
    if(auditors && auditors.length > 0 && auditors.includes(workerId)){
        return true;
    }
    return false;
}

// Helper function to check if user is assigned through individual or group assignment
export const filterProjectsByUserGroups = (qaRule: any, userRoles: any, workerId: string): boolean => {
    // Check individual assignment first
    if (qaRule.data.assignee_ids?.includes(workerId)) {
        return true;
    }

    // Check group assignment with a local, scoped predicate (no upfront map)
    const groupValue = qaRule.data?.group_id?.value;

    if (groupValue && userRoles?.some((role: any) => role.group_id === groupValue)) {
        return true;
    }

    const visibility: OptionEntry[] = qaRule.data.project_configuration?.audience?.groupVisibility;

    if(visibility && visibility.length > 0) {
        const groupVisibility: string[] = visibility.map(g => g.value);
        const userGroups: string[] = userRoles.map(role => role.group_id);
        const intersectionItems = new Set([...groupVisibility].filter(x => userGroups.includes(x)));

        if(intersectionItems.size > 0) {
            return true;
        }
    }

    return false;
}

// Helper function to check individual distribution visibility for cqa_coder only users
export const filterByIndividualDistribution = (qaRule: any, activeContextRoles: any, workerId: string): boolean => {
    // Check if user is cqa_coder but not cqa_manager
    const isCqaCoderOnly = activeContextRoles.includes('cqa_coder') && !activeContextRoles.includes('cqa_manager');
    
    if (!isCqaCoderOnly) {
        return true; // Not a coder-only user, don't filter
    }
    
    const audience = qaRule?.data?.project_configuration?.audience;
    
    // If distribution method is not 'individual', allow the project
    if (audience?.distributionMethod !== 'individual') {
        return true;
    }
    
    // Check if user is in individuals
    const isUserInIndividuals = audience?.individuals?.some(
        (coder: any) => coder?.value === workerId
    );
    
    return !!isUserInIndividuals;
}

// Main filter function using the helpers
const filterProjects = (qaRule: any, activeContextRoles: any, userRoles: any, workerId: string) => {
    // First check individual distribution visibility for cqa_coder only users
    if (workerId && !filterByIndividualDistribution(qaRule, activeContextRoles, workerId)) {
        return false;
    }
    
    // Check if user is assigned as an auditor
    if (filterProjectsByAuditors(qaRule, workerId)) {
        return true;
    }
    
    // Check if user is assigned through individual or group assignment
    if (filterProjectsByUserGroups(qaRule, userRoles, workerId)) {
        return true;
    }
    
    return false;
}

// Export the filterUtils object containing the helper functions
export const filterUtils = {
    filterProjectsByAuditors,
    filterProjectsByUserGroups,
    filterByIndividualDistribution,
    filterProjects
};

// Export filterProjects as default
export default filterProjects;
